import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Calendar,
  Layers,
  MapPin,
  Sparkles,
  Trash2,
} from 'lucide-react';
import dayjs from 'dayjs';
import { eventsApi } from './api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { GoalProgressCard } from './GoalProgressCard';
import { EventWeatherCard } from './EventWeatherCard';
import { PlanAnchors } from './PlanAnchors';
import { LiveMonitor } from './LiveMonitor';
import { EventClosingCard } from './EventClosingCard';
import { EventReportSurface } from './EventReportSurface';
import { DesignJourneySection } from './design-journey/DesignJourneySection';
import { PlannerTab } from '@/features/project/planner/PlannerTab';
import { ReportersPanel } from '@/features/reporter/ReportersPanel';
import { SharedQrManager } from '@/features/qr-group/SharedQrManager';
import { Skeleton } from '@/components/ui/Skeleton';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { EntityTypeBadge } from '@/components/ui/EntityTypeBadge';
import { DesignJourneyNudge } from './DesignJourneyNudge';
import { brandsApi } from '@/features/brands/api';
import { useT } from '@/lib/i18n/useT';
import { useFavoritable } from '@/features/favorites/FavoritableContext';
import { HelpHint } from '@/features/help/HelpHint';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { toast } from '@/components/ui/Toast';
import { eventPhase } from '@/features/project/eventPhase';
import { useRightSidebar } from '@/components/layout/RightSidebar';

type EventTab = 'plan' | 'survey' | 'track' | 'measure';

/**
 * Event detail — mirrors the Project workspace standard with PLAN / TRACK /
 * MEASURE tabs. PLAN reuses the same planner the Project uses, scoped to this
 * single event (drag xotskis / reporters / stations, edit properties, pick the
 * survey template) and adds the deep survey editor + eligible prize pool.
 */
export function EventDetailPage() {
  const { id: projectId, eventId } = useParams();
  const eId = Number(eventId);
  const navigate = useNavigate();
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [confirmarBorrado, setConfirmarBorrado] = useState(false);
  const queryClient = useQueryClient();

  const tab = (searchParams.get('tab') as EventTab) || 'plan';
  const setTab = (next: EventTab) => {
    const p = new URLSearchParams(searchParams);
    p.set('tab', next);
    setSearchParams(p, { replace: true });
  };

  const event = useQuery({
    queryKey: queryKeys.events.detail(eId),
    queryFn: () => eventsApi.getEvent(eId),
  });
  useFavoritable(event.data ? `Event · ${event.data.name}` : null);

  /*
   * Con el evento EN CURSO, el carril derecho empieza plegado.
   *
   * Durante el evento lo que se mira es el monitor y las estaciones, no las
   * propiedades del evento; el carril persistido abierto se comía un tercio
   * de la pantalla con el formulario de la última visita. Se pliega UNA vez
   * por evento, sin escribir la preferencia: si la persona lo vuelve a abrir,
   * se queda abierto, y en las demás pantallas su carril sigue como lo dejó.
   */
  const plegadoPara = useRef<number | null>(null);
  useEffect(() => {
    const ev = event.data;
    if (!ev || plegadoPara.current === ev.id) return;
    if (eventPhase(ev) === 'live') {
      plegadoPara.current = ev.id;
      useRightSidebar.getState().setOpen(false);
    }
  }, [event.data]);

  const brandId = event.data?.companyId ?? 0;
  const groupIdForBC = event.data?.eventGroupId ?? 0;
  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId),
    queryFn: () => brandsApi.get(brandId),
    enabled: brandId > 0,
  });
  const groupQ = useQuery({
    queryKey: queryKeys.eventGroups.detail(groupIdForBC),
    queryFn: () => eventsApi.getGroup(groupIdForBC),
    enabled: groupIdForBC > 0,
  });

  /*
   * ARCHIVAR EL EVENTO — la acción que faltaba.
   *
   * Se podía crear un evento y no había ninguna forma de retirarlo: ni aquí,
   * ni en el listado del proyecto. El proyecto y la estación sí la tenían
   * (`ProjectEditForm`, `StationForm`), y `eventsApi.deleteEvent` existía
   * desde el principio — sin que nada la llamara.
   *
   * Es un borrado BLANDO: `status: 'Deleted'`, como el del proyecto y el de
   * la estación. `BaseService` excluye ese estado de todos los listados y
   * `assertNotDeleted` corta la entrada por URL directa, así que el evento
   * desaparece de la aplicación pero sus respuestas siguen en la base — que
   * es justo lo que hace falta: lo recogido en un evento es el dato del
   * cliente, y una activación retirada no puede llevárselo por delante.
   *
   * **No arrastra sus estaciones**, igual que retirar un proyecto no arrastra
   * sus eventos: `BaseService.update` no cascadea y nadie lo hace por él. Y a
   * diferencia del proyecto, aquí sí se nota — `/stations` lista por
   * `companyId`, no por evento, así que las estaciones del evento retirado se
   * quedan ahí a la vista. El diálogo lo dice en vez de prometer lo contrario;
   * cascadearlas es una decisión de backend que el proyecto tiene pendiente
   * igual.
   */
  const archivar = useMutation({
    mutationFn: () => eventsApi.deleteEvent(eId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.events.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.stations.all });
      if (groupIdForBC > 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.eventGroups.detail(groupIdForBC) });
      }
      toast({
        tone: 'success',
        title: t('events.detail.deletedToast', { name: event.data?.name ?? '' }),
      });
      setConfirmarBorrado(false);
      // Al proyecto: quedarse en el detalle de algo que ya no existe da un
      // «no encontrado» que parece un fallo en vez de la consecuencia.
      navigate(projectId ? `/projects/${projectId}` : '/');
    },
    onError: (e: Error) =>
      toast({ tone: 'error', title: t('events.detail.deleteFailed'), description: e.message }),
  });

  if (event.isLoading) return <div className="page"><Skeleton className="h-40" /></div>;
  if (!event.data) return <p className="page">{t('events.detail.notFound')}</p>;

  const ev = event.data;

  return (
    <div className="page space-y-6">
      <Breadcrumbs
        className="print:hidden"
        items={[
          {
            label: brandQ.data?.name ?? t('breadcrumbs.brand'),
            to: brandId > 0 ? `/brand/${brandId}` : undefined,
          },
          {
            label: groupQ.data?.name ?? t('breadcrumbs.project'),
            to: projectId ? `/projects/${projectId}` : undefined,
          },
          { label: ev.name ?? t('breadcrumbs.event') },
        ]}
      />
      <button
        type="button"
        onClick={() => navigate(`/projects/${projectId}`)}
        className="inline-flex items-center gap-1 text-sm text-[var(--color-fg-muted)] hover:text-[var(--color-fg-primary)] print:hidden"
      >
        <ArrowLeft className="h-4 w-4" /> {t('events.detail.backToProject')}
      </button>

      <header className="flex flex-wrap items-start justify-between gap-4 print:hidden">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span
              className="rounded px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-[var(--color-fg-on-accent)]"
              style={{ backgroundColor: eventStatusColor(ev.eventStatus), fontFamily: 'var(--font-display)' }}
            >
              {/* Traducido, no crudo: la columna guarda «Active» y así se
                  quedaba en inglés con la app en español. */}
              {ev.eventStatus
                ? t(`events.status.${String(ev.eventStatus).toLowerCase()}`)
                : t('events.detail.unknown')}
            </span>
            {ev.codeRef && (
              <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--color-fg-muted)]">
                {ev.codeRef}
              </span>
            )}
          </div>
          <h1 className="flex items-center gap-2 text-3xl font-bold">
            <EntityTypeBadge type="event" />
            {ev.name}
            <HelpHint section="event-detail" />
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-[var(--color-fg-muted)]">
            {ev.address && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {ev.address}
              </span>
            )}
            {(ev.startDate || ev.endDate) && (
              <span className="inline-flex items-center gap-1.5 tabular-nums">
                <Calendar className="h-3.5 w-3.5" />
                {fmtEventDateTime(ev.startDate, ev.endDate, t('events.detail.tbd'))}
              </span>
            )}
          </div>
        </div>

        {/* El carril derecho de la cabecera: la acción sobre ESTE evento y,
            debajo, el tiempo que va a hacer (sept 2026: el clima sube del tab
            Plan a la cabecera, alineado a la derecha — es lo primero que se
            mira al abrir el evento, no algo que buscar pestaña abajo). */}
        <div className="flex min-w-0 flex-col items-end gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setConfirmarBorrado(true)}
            leftIcon={<Trash2 className="h-4 w-4" />}
          >
            {t('events.detail.delete')}
          </Button>
          <EventWeatherCard eventId={eId} compact />
        </div>
      </header>

      <ConfirmDialog
        open={confirmarBorrado}
        onOpenChange={setConfirmarBorrado}
        tone="danger"
        title={t('events.detail.deleteTitle', { name: ev.name ?? '' })}
        description={t('events.detail.deleteBody')}
        confirmLabel={t('events.detail.delete')}
        loading={archivar.isPending}
        onConfirm={() => archivar.mutate()}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as EventTab)}>
        <TabsList data-tour="event-tabs">
          <TabsTrigger value="plan" className="gap-2">
            <Layers className="h-4 w-4" /> {t('project.tab.plan')}
          </TabsTrigger>
          <TabsTrigger value="survey" className="gap-2">
            <Sparkles className="h-4 w-4" /> {t('events.detail.surveyTab')}
          </TabsTrigger>
          <TabsTrigger value="track" className="gap-2">
            <Activity className="h-4 w-4" /> {t('project.tab.track')}
          </TabsTrigger>
          <TabsTrigger value="measure" className="gap-2">
            <BarChart3 className="h-4 w-4" /> {t('project.tab.measure')}
          </TabsTrigger>
        </TabsList>

        {/* PLAN — the project planner, scoped to this one event. What's set
            in "Events in planning" is the source of truth for this event. */}
        <TabsContent value="plan">
          {/* El siguiente paso del flujo, dicho en pantalla: con las
              estaciones montadas, lo que toca es el Design Journey — y vive
              en otra pestaña, donde nadie lo encontraba solo. */}
          <DesignJourneyNudge eventId={eId} onGo={() => setTab('survey')} />
          {/* El índice de la pestaña. Va PRIMERO y se queda pegado arriba:
              la lista creció hasta esconder lo de abajo, y nadie baja
              buscando algo que no sabe que está. */}
          <PlanAnchors
            anclas={[
              { id: 'plan-stations', etiqueta: t('plan.anchors.stations') },
              // El clima ya no es una sección del Plan: vive en la cabecera.
              { id: 'plan-goals', etiqueta: t('plan.anchors.goals') },
              { id: 'plan-staff', etiqueta: t('plan.anchors.staff') },
            ]}
          />
          <div id="plan-stations" className="scroll-mt-24">
          <PlannerTab
            groupId={groupIdForBC}
            brandId={brandId}
            companyId={ev.companyId ?? undefined}
            projectStart={groupQ.data?.startDate}
            projectEnd={groupQ.data?.endDate}
            events={[ev]}
            totalEventCount={1}
            focusEventId={eId}
            onLimitReached={() => setUpgradeOpen(true)}
            singleEvent
            // Shared QR sits under the event block (left column), aligned with
            // it rather than stretching across the whole tab.
            leftFooter={<SharedQrManager eventId={eId} brandId={brandId} />}
          />
          </div>
          {/* Task 5.1 — goals are set where the work is planned, not where it
              is watched. Track is for what is happening right now. */}
          <div id="plan-goals" className="mt-6 scroll-mt-24">
            <GoalProgressCard eventId={eId} groupId={groupIdForBC} />
          </div>
          {/* Quién atiende el stand, junto a lo demás que se decide antes del
              evento. El panel existía terminado y no lo montaba ninguna
              pantalla: la única forma de dar de alta a un reportero era
              arrastrarlo en el lienzo del planner, y eso deja el enlace —que
              caduca, se reemite y se revoca— sin ningún sitio donde mirarlo.
              El plan gratuito lo ve en gris con su invitación a subir; eso lo
              resuelve el propio panel. */}
          {/* El hueco de abajo no es un descuido: sin él, la ÚLTIMA sección
              no puede subir hasta la barra —no queda scroll por debajo— y el
              salto a «Personal» apenas movía la página. Con él, todas las
              anclas se comportan igual. */}
          <div id="plan-staff" className="mt-6 scroll-mt-24 pb-[50vh]">
            <ReportersPanel eventId={eId} />
          </div>
        </TabsContent>

        {/* ENCUESTA — arriba, elegir y desplegar; debajo, Design Journey:
            el estándar de UI que heredarán Xamplin y DiD·ox. El editor por
            evento antiguo se retiró: lo que ve el invitado sale del despliegue,
            así que aquel guardaba sin efecto. */}
        <TabsContent value="survey">
          <DesignJourneySection eventId={eId} />
        </TabsContent>

        {/* TRACK — current state only. The live monitor is this tab, expanded
            via its own fullscreen control, not a page you navigate away to. */}
        <TabsContent value="track">
          <div className="space-y-4">
            <EventClosingCard eventId={eId} />
            <LiveMonitor eventId={eId} />
          </div>
        </TabsContent>

        {/* REPORT — one surface: the report, prize redemption and cost per
            result, with Print and Share. */}
        <TabsContent value="measure">
          <EventReportSurface eventId={eId} />
        </TabsContent>
      </Tabs>

      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="xotskiStations"
        currentPlanName="Free"
        limit={0}
      />
    </div>
  );
}

function eventStatusColor(status: string | null | undefined): string {
  switch (status) {
    case 'Active':
      return 'var(--color-xk-accent)';
    case 'Finished':
      return 'var(--color-xk-text-muted)';
    case 'Disabled':
      return 'var(--color-warn)';
    default:
      return 'var(--color-fg-muted)';
  }
}

/**
 * Hour-resolution range. Same-day events collapse to a single date with two
 * times; different days show full date+time on each side.
 */
function fmtEventDateTime(start?: string | null, end?: string | null, tbd = 'TBD'): string {
  if (!start && !end) return tbd;
  const s = start ? dayjs(start) : null;
  const e = end ? dayjs(end) : null;
  if (s && e) {
    if (s.isSame(e, 'day')) {
      return `${s.format('MMM D, YYYY h:mm A')} – ${e.format('h:mm A')}`;
    }
    return `${s.format('MMM D, YYYY h:mm A')} – ${e.format('MMM D, YYYY h:mm A')}`;
  }
  return (s ?? e)!.format('MMM D, YYYY h:mm A');
}
